import { useEffect, useRef, useState } from 'react';

import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation, useQuery } from '@redwoodjs/web';

import { SelectBaseField } from 'src/components/common/form/FormSelect';
import TextEditor from 'src/components/common/TextEditor';
import { FileUploadDropDown, NPIAutoComplete } from 'src/components/FormikComponents';
import { AutoCompleteOptionType } from 'src/components/FormikComponents/NPIAutoComplete';
import Loader from 'src/components/Loader/Loader';
import MainCard from 'src/components/MainCard/MainCard';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { UPDATE_PRODUCT_MUTATION } from 'src/graphql/mutations/products.mutation';
import { GET_PRODUCT } from 'src/graphql/queries/product.query';
import useGeneratePresignedUrl from 'src/hooks/useGeneratePresignedUrl';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { countryConfig } from 'src/lib/countryConfig';
import { formatProductId } from 'src/utils/utils';

import { DELETE_UPLOAD_URL } from '../AddProductPage/AddProductPage';

type EditProductProps = {
    id: string;
};

interface UpdateProductInput {
    status: string;
    description?: { text: string; type: string }[];
    imageUrl?: string;
    countries?: string[];
    productFile?: {
        locationUrl: string;
    };
}

const EditProductPage = ({ id }: EditProductProps) => {
    const timeout = useRef(null);
    const [uploadUrl, setUploadUrl] = useState('');
    const [removeUploadImage, setRemoveUploadImage] = useState(false);
    const [uploadErrorMessage, setUploadErrorMessage] = useState('');
    const [descriptionTextValues, setDescriptionTextValues] = useState();
    const [descriptionErrorMessage, setDescriptionErrorMessage] = useState('');
    const [newImageUrl, setNewImageUrl] = useState('');
    const [countries] = useState([
        {
            id: 1,
            label: 'ALL',
            value: 'ALL',
        },
        {
            id: 2,
            label: countryConfig.IE.name,
            value: countryConfig.IE.name,
        },
        {
            id: 3,
            label: countryConfig.GB.name,
            value: countryConfig.GB.name,
        },
        {
            id: 4,
            label: countryConfig.XI.name,
            value: countryConfig.XI.name,
        },
    ]);

    const productDocTypes = [
        {
            key: 'product',
            value: 'Product',
        },
    ];

    const onSubmit = async (input: UpdateProductInput) => {
        const data = { ...input };

        const newImage = input.productFile?.locationUrl ?? '';
        if (removeUploadImage) data.imageUrl = newImage;
        delete data.productFile;

        if (!data.imageUrl) {
            setUploadErrorMessage('Product image is required');
            return;
        }

        data.description = descriptionTextValues;
        if (!data.description) {
            setDescriptionErrorMessage('Description is required');
            return;
        }

        data.countries = (input.countries as unknown as AutoCompleteOptionType[]).map((country) => {
            return country.value as string;
        });

        await updateProduct({ variables: { id, input: data } });
    };

    const [updateProduct, { loading }] = useMutation(UPDATE_PRODUCT_MUTATION, {
        onCompleted: (data) => {
            showSuccessToast(`${data.updateProduct.name} product updated successfully`);
            timeout.current = setTimeout(() => {
                return navigate('/products');
            }, 1000);
        },
        refetchQueries: [{ query: GET_PRODUCT, variables: { id } }],
        onError: (error) => {
            showErrorToast(error.message);
        },
    });

    const [deleteUploadURL] = useMutation(DELETE_UPLOAD_URL, {
        onCompleted: () => {
            showSuccessToast('Upload remove successfully');
            setUploadUrl('');
        },
        onError: (error) => {
            showErrorToast(error.message);
        },
    });

    useEffect(() => {
        return () => {
            return clearTimeout(timeout.current);
        };
    }, [timeout]);

    useEffect(() => {
        if (uploadUrl.length) {
            setNewImageUrl(uploadUrl);
            setUploadErrorMessage('');
        }
    }, [uploadUrl]);

    const { loading: productLoading, data: productData } = useQuery(GET_PRODUCT, {
        variables: { id },
    });

    const { validateAwsURL } = useGeneratePresignedUrl();
    const [presignedImageUrl, setPresignedImageUrl] = useState<string | null>(null);

    useEffect(() => {
        const fetchPresignedUrl = async () => {
            if (productData?.product?.imageUrl) {
                const url = await validateAwsURL(productData.product.imageUrl);
                setPresignedImageUrl(url ?? productData.product.imageUrl);
            }
        };

        fetchPresignedUrl();
    }, [productData?.product?.imageUrl]);

    if (productLoading) return <Loader />;

    const { product } = productData;

    const removeUploadUrl = async () => {
        await deleteUploadURL({
            variables: {
                url: uploadUrl.toString() || `${product?.imageUrl}`,
            },
        });
        setUploadUrl('');
        setUploadErrorMessage('');
        setRemoveUploadImage(true);
    };

    const initialValues = {
        status: product.status,
        imageUrl: product.imageUrl,
        countries: product.countries.map(({ name }, index) => {
            return {
                id: index + 1,
                label: name,
                value: name,
            };
        }),
    };

    const validationSchema = Yup.object().shape({
        status: Yup.string().optional(),
        imageUrl: Yup.string().optional().nullable(),
        countries: Yup.array()
            .of(Yup.object())
            .required('Country is required')
            .min(1, 'Country is required'),
    });

    return (
        <PageContent heading="Update Product Catalogue">
            <MetaTags title="Edit Product" description="Edit Product" />
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {(props: FormikProps<UpdateProductInput>) => {
                    return (
                        <Form>
                            <Stack spacing={5}>
                                <MainCard>
                                    <h2 className="font-normal mb-0 mt-10">Product Information</h2>
                                    <Box className="mt-20">
                                        <Stack spacing={4} sx={{ marginBottom: '40px' }}>
                                            <Box>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={6}>
                                                        <Stack spacing={4}>
                                                            <Box>
                                                                <label className="font-bold">
                                                                    Product ID
                                                                </label>
                                                                <Box className="mt-10">
                                                                    <Typography
                                                                        variant="h3"
                                                                        className="mt-10 font-normal black-200"
                                                                    >
                                                                        {formatProductId(
                                                                            product?.type,
                                                                            product?.id
                                                                        )}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            <Box>
                                                                <label className="font-bold">
                                                                    Product Name
                                                                </label>
                                                                <Typography
                                                                    variant="h3"
                                                                    className="mt-10 font-normal black-200"
                                                                >
                                                                    {product?.name}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <label className="font-bold">
                                                                    Product Catalogue
                                                                </label>
                                                                <Typography
                                                                    variant="h3"
                                                                    className="mt-10 font-normal black-200"
                                                                >
                                                                    {product?.catalogue}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <label className="font-bold">
                                                                    Product Type
                                                                </label>
                                                                <Typography
                                                                    variant="h3"
                                                                    className="mt-10 font-normal black-200"
                                                                >
                                                                    {product?.type}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <NPIAutoComplete
                                                                    header="Country"
                                                                    placeholder="Select Country"
                                                                    name="countries"
                                                                    multiple
                                                                    required
                                                                    options={countries}
                                                                />
                                                            </Box>
                                                        </Stack>
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <Stack spacing={4}>
                                                            <Box>
                                                                <SelectBaseField
                                                                    label="Product Status"
                                                                    name="status"
                                                                    inputLabel={`${product?.status}`}
                                                                    initialValue={`${product?.status}`}
                                                                    menuItems={[
                                                                        {
                                                                            key: 'ACTIVE',
                                                                            value: 'ACTIVE',
                                                                        },
                                                                        {
                                                                            key: 'DEACTIVATED',
                                                                            value: 'DEACTIVATED',
                                                                        },
                                                                    ]}
                                                                />
                                                            </Box>
                                                            <Box>
                                                                {product?.description && (
                                                                    <TextEditor
                                                                        label="Product Description"
                                                                        setTextValues={
                                                                            setDescriptionTextValues
                                                                        }
                                                                        errorMessage={
                                                                            descriptionErrorMessage
                                                                        }
                                                                        initialValue={
                                                                            product?.description
                                                                        }
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Stack>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                            <Box>
                                                {(newImageUrl || presignedImageUrl) &&
                                                !removeUploadImage ? (
                                                    <Box>
                                                        <img
                                                            src={`${
                                                                newImageUrl || presignedImageUrl
                                                            }`}
                                                            alt="upload"
                                                            width="120px"
                                                        />
                                                        <Button
                                                            sx={{ margin: '-10px 0 0 20px' }}
                                                            onClick={removeUploadUrl}
                                                        >
                                                            Update
                                                        </Button>
                                                    </Box>
                                                ) : (
                                                    <>
                                                        <FileUploadDropDown
                                                            name="productFile"
                                                            fileTypes={productDocTypes.map(
                                                                ({ key, value }) => {
                                                                    return {
                                                                        value,
                                                                        label: key,
                                                                    };
                                                                }
                                                            )}
                                                            label="Product Image"
                                                        />
                                                        <label className="error">
                                                            {uploadErrorMessage}
                                                        </label>
                                                    </>
                                                )}
                                            </Box>
                                        </Stack>
                                    </Box>
                                </MainCard>
                                <Box>
                                    <div className="flex space-end">
                                        <Button
                                            onClick={() => {
                                                return navigate('/products');
                                            }}
                                            className="reset-btn btn-medium mr-20"
                                            variant="contained"
                                            size="medium"
                                            disableElevation
                                        >
                                            Cancel
                                        </Button>
                                        {loading ? (
                                            <LoadingButton
                                                className="btn-medium"
                                                style={{
                                                    backgroundColor: '#F18F34',
                                                    color: '#fff',
                                                    padding: '5px 30px 5px 10px',
                                                }}
                                                loading
                                                loadingPosition="start"
                                                startIcon={<SaveIcon />}
                                                variant="contained"
                                            >
                                                Save
                                            </LoadingButton>
                                        ) : (
                                            <Box className="btn-wrapper">
                                                <Button
                                                    className={`btn-primary btn-medium`}
                                                    disabled={loading}
                                                    type="submit"
                                                    variant="contained"
                                                    size="medium"
                                                    disableElevation
                                                >
                                                    Save
                                                </Button>
                                            </Box>
                                        )}
                                    </div>
                                </Box>
                            </Stack>
                        </Form>
                    );
                }}
            </Formik>
        </PageContent>
    );
};

export default EditProductPage;
